import type { MapId } from '~/_shared/types/map';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  BoundaryTerritoryGroupConnectedEntities, BoundaryTerritoryType,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import {
  convertBTGMatchupRequestToResponse, convertBTGSettingsRequestToResponse, createTerritoryBoundaryGroupModelFromResponse,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.factory';
import type { BoundaryTerritoryGroupModelResponse } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import type {
  DataLayer, DataLayerId,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';
import {
  type CreateBoundaryTerritoryGroupParams, createBoundaryTerritoryGroupRequestParams,
} from '../boundary/boundaryTerritoryGroup.helpers';

export const DATA_LAYER_TOOL_DEFAULT_NUMBER_OF_RANGES = 5;

export const isDataLayersArtificialBoundaryTerritoryGroup = (boundaryTerritoryGroup: BoundaryTerritoryGroup): boolean => {
  return boundaryTerritoryGroup.settings.connectedEntityData?.entity === BoundaryTerritoryGroupConnectedEntities.DataLayer;
};

export const createDataLayerDemographicBTGRequestParams = ({
  dataLayerId,
  mapId,
  boundaryGroupId,
  demographicId,
  numberOfRanges,
  metrics,
  style,
  disableMapInteraction,
}: Readonly<{
  dataLayerId: DataLayerId;
  mapId: MapId;
  boundaryGroupId: BoundaryGroupId;
  demographicId: string;
  numberOfRanges?: number;
  metrics: CreateBoundaryTerritoryGroupParams['metrics'];
  style?: CreateBoundaryTerritoryGroupParams['style'];
  disableMapInteraction?: boolean;
}>) =>
  createBoundaryTerritoryGroupRequestParams({
    mapId,
    boundaryGroupId,
    territoryType: BoundaryTerritoryType.Demographic,
    demographicId,
    numberOfRanges,
    connectedEntityData: {
      entity: BoundaryTerritoryGroupConnectedEntities.DataLayer,
      id: dataLayerId,
    },
    metrics,
    style,
    disableMapInteraction,
  });

export const createSampleBTGForDataLayer = ({
  dataLayer,
  numberOfRanges,
}: {
  dataLayer: DataLayer;
  numberOfRanges?: number;
}): BoundaryTerritoryGroup => {
  const sampleMapId = 0;

  const request = createDataLayerDemographicBTGRequestParams({
    dataLayerId: dataLayer.id,
    mapId: sampleMapId,
    boundaryGroupId: 0,
    demographicId: 'sample-demographic-id',
    numberOfRanges: numberOfRanges ?? dataLayer.settings.numberOfRanges ?? DATA_LAYER_TOOL_DEFAULT_NUMBER_OF_RANGES,
    metrics: [],
    style: {
      line_width: dataLayer.settings.style.lineWidth,
      line_color: dataLayer.settings.style.lineColor,
      gradient_colors: dataLayer.settings.style.gradientColors,
    },
  });

  const itemResponse: BoundaryTerritoryGroupModelResponse = {
    map_id: 0,
    boundary_group_id: 0,
    archived_at: null,
    matchings: convertBTGMatchupRequestToResponse(request.matchings),
    settings: convertBTGSettingsRequestToResponse(request.settings, []),
  };

  return {
    ...createTerritoryBoundaryGroupModelFromResponse(itemResponse),
    boundaryTerritoryGroupId: 0,
  };
};
